<template>
  <AwModal
    :open="open"
    title="Vérifier le document"
    size="md"
    position="right"
    @update:open="if ($event === false) handleClose();"
    @close-prevent="handleClose"
  >
    <template #leftPanel>
      <FilePreview v-if="selectedFile" :file="selectedFile" class="min-w-125" />
    </template>

    <template #body>
      <!-- Files Section -->
      <section class="section">
        <!-- Title -->
        <h2 class="text-secondary-900 font-semibold text-lg mb-3 truncate">{{ title }}</h2>

        <!-- Info banner (configured per document type) -->
        <div v-if="adapter.banner" class="info-banner">
          <img :src="adapter.banner.illustrationSrc" alt="" class="info-banner__illustration" />
          <div class="info-banner__content">
            <p class="info-banner__title">{{ adapter.banner.title }}</p>
            <p class="info-banner__text">{{ adapter.banner.text }}</p>
          </div>
        </div>

        <!-- Document status badges (shown when re-verifying existing documents) -->
        <DocumentStatusBadge
          v-if="lifecycleStatus"
          :lifecycle-status="lifecycleStatus"
          :days-before-expiration="daysBeforeExpiration"
          :expiration-date="expirationDate"
          :internal-reviews="internalReviews"
          :external-reviews="externalReviews"
          class="mb-3"
        />

        <!-- Verification banner (when verifying document added by talent) -->
        <AwBanner
          v-if="showVerificationBanner"
          color="critical"
          icon="i-ph-hand-eye-fill"
          text="Le document a été ajouté par l'intérimaire. Vérifie et valide les informations."
          class="mb-4"
        />

        <!-- File limit warning -->
        <AwBanner
          v-if="fileLimitWarning"
          color="warning"
          icon="i-ph-warning"
          :text="fileLimitWarning"
          is-closable
          class="mb-2"
          @close="emit('dismiss-file-limit-warning')"
        />

        <!-- File MIME type warning -->
        <AwBanner
          v-if="fileMimeTypeWarning"
          color="warning"
          icon="i-ph-warning"
          :text="fileMimeTypeWarning"
          is-closable
          class="mb-2"
          @close="emit('dismiss-file-mime-type-warning')"
        />

        <!-- No files error (blocking) -->
        <AwBanner
          v-if="hasNoFiles"
          color="critical"
          icon="i-ph-x-circle"
          text="Aucun fichier sélectionné. Ajoute au moins un fichier."
          class="mb-2"
        />

        <!-- Error State -->
        <AwBanner v-if="extractionError" color="warning" icon="i-ph-warning" :text="extractionError" class="mb-2" />

        <div class="file-preview-list">
          <FileItem
            v-for="(file, index) in files"
            :key="index"
            :file="file"
            is-removable
            :is-selected="index === selectedFileIndex"
            @click="selectedFileIndex = index"
            @on-remove="handleRemoveFile(index)"
          />
          <AwButton
            leading-icon="i-ph-plus"
            size="lg"
            color="secondary"
            variant="outline"
            :disabled="files.length >= maxFiles"
            class="justify-center"
            @click="emit('add-files-requested')"
          >
            Ajouter un fichier
          </AwButton>
        </div>
      </section>

      <!-- Validation Form Section -->
      <section v-if="files.length > 0" class="section">
        <h3 class="text-secondary-900 font-semibold mb-3">Données à vérifier</h3>

        <!-- Loading State -->
        <AwBanner
          v-if="isLoading"
          is-loading
          color="primary"
          icon="i-ph-spinner-gap-bold"
          class="mb-2"
          text="Données en cours d'extraction..."
        />

        <!-- Form -->
        <div v-else class="validation-form">
          <!-- Warnings without a matching form field (e.g. a coherence check on data that isn't user-editable) -->
          <AwBanner
            v-for="(message, key) in unassignedWarnings"
            :key="key"
            :text="message"
            icon="i-ph-warning"
            color="warning"
          />

          <div v-for="field in visibleFields" :key="String(field.key)">
            <AwFormField
              :label="field.label"
              :help="field.description"
              :error-message="fieldErrors[String(field.key)] || undefined"
              :required="adapter.requiredFields.includes(field.key)"
            >
              <!-- Select Field -->
              <AwSelectMenu
                v-if="field.type === 'select'"
                :model-value="formData[String(field.key)]"
                :items="field.options"
                value-key="value"
                label-key="label"
                :disabled="field.disabled"
                :color="fieldErrors[String(field.key)] ? 'critical' : 'default'"
                class="w-full"
                :search-input="field.isSearchable"
                @update:model-value="(value) => (formData[String(field.key)] = value)"
              />

              <!-- Radio Field -->
              <AwRadioGroup
                v-else-if="field.type === 'radio'"
                :model-value="formData[String(field.key)]"
                :items="field.options"
                :disabled="field.disabled"
                variant="card"
                orientation="horizontal"
                fill
                @update:model-value="(value) => (formData[String(field.key)] = String(value ?? ''))"
              />

              <!-- Text Field -->
              <div v-else-if="field.type === 'text'" class="flex items-center gap-2">
                <AwTextField
                  :model-value="getTextFieldDisplayValue(field)"
                  type="text"
                  :placeholder="field.placeholder"
                  :disabled="field.disabled"
                  :color="fieldErrors[String(field.key)] ? 'critical' : 'default'"
                  class="w-full"
                  @update:model-value="
                    (value) => (formData[String(field.key)] = field.parse?.(String(value)) ?? value ?? '')
                  "
                />
                <Copy v-if="field.copyable && formData[String(field.key)]" :text="getTextFieldDisplayValue(field)" />
              </div>

              <!-- IBAN Field -->
              <AwTextField
                v-else-if="field.type === 'iban'"
                :model-value="formData[String(field.key)]"
                type="text"
                :placeholder="field.placeholder"
                :disabled="field.disabled"
                :color="fieldErrors[String(field.key)] ? 'critical' : 'default'"
                class="w-full"
                @update:model-value="(value) => (formData[String(field.key)] = value ?? '')"
              />

              <!-- Date Field -->
              <AwTextField
                v-else-if="field.type === 'date'"
                :model-value="formData[String(field.key)]"
                type="date"
                :placeholder="field.placeholder"
                :disabled="field.disabled"
                :color="fieldErrors[String(field.key)] ? 'critical' : 'default'"
                class="w-full"
                @update:model-value="(value) => (formData[String(field.key)] = value ?? '')"
              />

              <!-- Address Field -->
              <LocationSelectV2
                v-else-if="field.type === 'address'"
                :value="addressPrefills[String(field.key)]"
                :multiple="false"
                :search-types="['street']"
                :title="field.placeholder ?? 'Rechercher une adresse'"
                :disabled="field.disabled"
                @update:value="(location) => handleAddressSelected(String(field.key), location)"
              />

              <!-- Checkbox Field -->
              <AwCheckbox
                v-else-if="field.type === 'checkbox'"
                :model-value="formData[String(field.key)] === 'true'"
                :disabled="field.disabled"
                :label="field.checkboxLabel"
                :description="field.checkboxDescription"
                variant="card"
                @update:model-value="(value) => (formData[String(field.key)] = value ? 'true' : 'false')"
              />
            </AwFormField>
            <!-- Field warning (non-blocking) -->
            <AwBanner
              v-if="fieldWarnings[String(field.key)]"
              :text="fieldWarnings[String(field.key)]"
              icon="i-ph-warning"
              color="warning"
              class="mt-2"
            >
            </AwBanner>
          </div>
        </div>
      </section>
    </template>

    <template #footer>
      <AwButton v-if="!showVerificationBanner" color="secondary" variant="ghost" @click="handleClose">
        Annuler
      </AwButton>
      <AwButton
        v-if="showVerificationBanner"
        color="critical"
        :disabled="isStoring || isRejecting || hasNoFiles"
        :loading="isRejecting"
        @click="handleReject"
      >
        Rejeter
      </AwButton>
      <AwButton
        :color="showVerificationBanner ? 'success' : 'secondary'"
        :disabled="isLoading || isStoring || isRejecting || hasValidationErrors || hasNoFiles"
        @click="handleValidate()"
      >
        {{ isStoring ? "Enregistrement..." : "Valider" }}
      </AwButton>
    </template>
  </AwModal>
</template>

<script setup lang="ts">
import { useDebounceFn } from "@vueuse/core";
import { AwCheckbox, AwFormField, AwRadioGroup, AwTextField } from "@asap/design";
import type { LocationFilter, LocationsFilter } from "@asap/shared/schemas/list";
import { toAddressPrefill } from "~/utils/address";
import type { AnyDocumentTypeHandler } from "../handlers/handler";
import type { BaseValidationField } from "../handlers/types";
import DocumentStatusBadge from "../record/DocumentStatusBadge.vue";
import { toFormValues } from "../utils";
import type { Props as DocumentStatusBadgeProps } from "../record/DocumentStatusBadge.vue";

type BaseProps = {
  open: boolean;
  title: string;
  files: File[];
  maxFiles: number;
  formFields: readonly BaseValidationField[];
  adapter: AnyDocumentTypeHandler;
  initialData?: Record<string, unknown>;
  isLoading?: boolean;
  isStoring?: boolean;
  isRejecting?: boolean;
  extractionError?: string | null;
  fileLimitWarning?: string | null;
  fileMimeTypeWarning?: string | null;
  context?: Record<string, unknown>;
  showVerificationBanner?: boolean;
};

type Props = BaseProps & DocumentStatusBadgeProps;

type Emits = {
  "update:open": [value: boolean];
  complete: [data: Record<string, string>];
  reject: [data: Record<string, unknown>];
  cancel: [];
  "request-close": [];
  "add-files-requested": [];
  "remove-file": [index: number];
  "dismiss-file-limit-warning": [];
  "dismiss-file-mime-type-warning": [];
};

const props = withDefaults(defineProps<Props>(), {
  initialData: () => ({}),
  context: () => ({}),
  isLoading: false,
  isStoring: false,
  isRejecting: false,
  extractionError: undefined,
  fileLimitWarning: undefined,
  fileMimeTypeWarning: undefined,
  showVerificationBanner: false,
  lifecycleStatus: undefined,
  daysBeforeExpiration: undefined,
  expirationDate: undefined,
  internalReviews: () => [],
  externalReviews: () => [],
});

const emit = defineEmits<Emits>();

const formData = ref<Record<string, string>>(toFormValues(props.initialData, props.formFields));
const fieldErrors = ref<Record<string, string>>({});
const fieldWarnings = ref<Record<string, string>>({});

const getTextFieldDisplayValue = (field: Extract<BaseValidationField, { type: "text" }>): string => {
  const raw = formData.value[String(field.key)];
  return field.format?.(raw) ?? raw;
};

// Initial address values shown in the picker, kept separate so they don't overwrite what the user selects.
const addressPrefills = ref<Record<string, LocationFilter | undefined>>({});

function seedAddressPrefills() {
  for (const field of props.formFields) {
    if (field.type !== "address") continue;
    addressPrefills.value[String(field.key)] = toAddressPrefill(formData.value[String(field.key)]);
  }
}
seedAddressPrefills();

function handleAddressSelected(key: string, location: LocationsFilter | LocationFilter | undefined) {
  const selected = Array.isArray(location) ? location[0] : location;
  formData.value[key] = selected?.label ?? "";
}

// Filter fields by visibility predicate
const visibleFields = computed(() => props.formFields.filter((f) => !f.visible || f.visible(formData.value)));

// Warnings keyed by a field that isn't shown in the form (e.g. a coherence check on non-editable data)
const unassignedWarnings = computed(() => {
  const visibleKeys = new Set(visibleFields.value.map((f) => String(f.key)));
  return Object.fromEntries(Object.entries(fieldWarnings.value).filter(([key]) => !visibleKeys.has(key)));
});

// Clear values of fields that become hidden to avoid submitting stale data
watch(visibleFields, (newFields, oldFields) => {
  const visibleKeys = new Set(newFields.map((f) => String(f.key)));
  for (const field of oldFields ?? []) {
    const key = String(field.key);
    if (!visibleKeys.has(key)) {
      formData.value[key] = "";
    }
  }
});

// Helper to convert Partial record to Record<string, string> by filtering undefined values
const toFieldMessages = (partial: Partial<Record<PropertyKey, string>>): Record<string, string> => {
  return Object.fromEntries(
    Object.entries(partial).filter((entry): entry is [string, string] => entry[1] !== undefined)
  );
};

// File preview state
const selectedFileIndex = ref(0);

// Computed properties for file preview
const selectedFile = computed(() => props.files[selectedFileIndex.value]);

// Handle file removal with smart selection update
const handleRemoveFile = (indexToRemove: number) => {
  // Case 1: Deleted file is before the selected file
  // The selected file shifts down by one index
  if (indexToRemove < selectedFileIndex.value) {
    selectedFileIndex.value--;
  }
  // Case 2: Deleted file is the currently selected file
  // Select the next file, or previous if it was the last
  else if (indexToRemove === selectedFileIndex.value) {
    if (indexToRemove === props.files.length - 1) {
      // Last file being deleted: select the previous file
      selectedFileIndex.value = Math.max(0, indexToRemove - 1);
    }
    // Otherwise keep same index (next file will shift into this position)
  }
  // Case 3: Deleted file is after the selected file
  // No change needed - selected file remains at same index

  emit("remove-file", indexToRemove);
};

// Sync with initial data when it changes (after OCR)
watch(
  () => props.initialData,
  (newData) => {
    if (newData) {
      formData.value = toFormValues(newData, props.formFields);
      seedAddressPrefills();
      // Clear errors when new data arrives
      fieldErrors.value = {};
    }
  }
);

// Debounced validation API call (300ms)
const debouncedValidate = useDebounceFn(async () => {
  try {
    const errors = await props.adapter.validateFields(formData.value, props.context ?? {});
    fieldErrors.value = toFieldMessages(errors);
  } catch (error) {
    // Silent failure for real-time validation
    console.warn("Real-time validation failed:", error);
  }
}, 300);

// Debounced warning API call (300ms)
const debouncedWarn = useDebounceFn(async () => {
  try {
    const warnings = await props.adapter.warn(formData.value, props.context ?? {});
    fieldWarnings.value = toFieldMessages(warnings);
  } catch (error) {
    // Silent failure for warnings
    console.warn("Real-time warnings failed:", error);
  }
}, 300);

// Watch form data changes for real-time validation and warnings
watch(
  formData,
  () => {
    debouncedValidate();
    debouncedWarn();
  },
  { deep: true }
);

// Check if form has any errors
const hasValidationErrors = computed(() => {
  return Object.values(fieldErrors.value).some((error) => error !== undefined && error !== "");
});

// Check if no files are present
const hasNoFiles = computed(() => props.files.length === 0);

const handleClose = () => {
  emit("request-close");
};

const forceClose = () => {
  emit("update:open", false);
  emit("cancel");
};

defineExpose({ forceClose });

const handleReject = () => {
  if (props.files.length === 0) return;
  emit("reject", formData.value);
};

const handleValidate = async () => {
  // Step 0: Check files exist (defensive check)
  if (props.files.length === 0) {
    return; // Button should be disabled, but safety net
  }

  // Step 1: Final validation via API
  try {
    const errors = await props.adapter.validateFields(formData.value, props.context ?? {});

    if (Object.keys(errors).length > 0) {
      // Display validation errors
      fieldErrors.value = toFieldMessages(errors);
      return;
    }

    // Data is validated - emit to parent for storage
    // Parent will handle closing the dialog after successful storage
    emit("complete", formData.value);
  } catch (error) {
    console.error("Final validation failed:", error);
    // Show a generic error (could be improved with better error handling)
    fieldErrors.value = {
      _form: "Une erreur est survenue lors de la validation. Veuillez réessayer.",
    };
  }
};
</script>

<style scoped lang="scss">
// Main content sections
.section {
  padding: 1rem;
}

.file-preview-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.validation-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.info-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px;
  margin-bottom: 0.75rem;
  background-color: #f7f7f7;
  border-radius: 12px;

  &__illustration {
    flex-shrink: 0;
    width: 70px;
    height: 70px;
    object-fit: contain;
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  &__title {
    font-size: 13px;
    font-weight: 600;
    line-height: 16px;
    color: #66001e;
  }

  &__text {
    font-size: 12px;
    font-weight: 500;
    line-height: 15px;
    color: #60676d;
  }
}
</style>
